import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from '@/types';
import { apiFetch } from '@/lib/api';
import { toast } from 'sonner';

interface AuthContextType {
  user: User | null;
  profile: User | null;
  loading: boolean;
  accessExpiresAt: number | null;
  lastAuthValidation: number | null;
  sessionHealthy: boolean;
  login: (email: string, password: string, turnstileToken?: string) => Promise<{ mfa_required?: boolean; user_id?: string; requiresCaptcha?: boolean } | void>;
  verifyMfa: (userId: string, code: string, isBackupCode?: boolean, turnstileToken?: string) => Promise<{ requiresCaptcha?: boolean } | void>;
  signup: (email: string, password: string, username: string, turnstileToken?: string) => Promise<{ requiresCaptcha?: boolean } | void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  updateUsername: (username: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  updateUserPlan: (plan: 'FREE' | 'PRO' | 'ELITE' | 'ENTERPRISE') => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<User | null>(() => {
    try {
      const savedUser = localStorage.getItem('user_profile_cache');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (e) {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [lastRefreshTime, setLastRefreshTime] = useState<number>(Date.now());
  const lastRefreshRef = React.useRef<number>(Date.now());
  const lastMeFetchRef = React.useRef<number>(0);
  const [accessExpiresAt, setAccessExpiresAt] = useState<number | null>(null);
  const [lastAuthValidation, setLastAuthValidation] = useState<number | null>(null);

  // Inverted: Check health based on last successful refresh and server-provided expiry
  // UI ONLY: the server remains the ultimate source of truth
  const sessionHealthy = user ? (accessExpiresAt ? accessExpiresAt * 1000 > Date.now() : true) : false;

  const sanitizeUserForCache = (userData: User): Partial<User> => {
    return {
      username: userData.username,
      email: userData.email,
      plan: userData.plan,
      picture: userData.picture,
      public_id: userData.public_id,
      total_score: userData.total_score,
      total_play_time: userData.total_play_time,
      studio_time: userData.studio_time,
    };
  };

  const showSuspendedAlert = () => {
    toast.error('Your account is suspended due to violation of the policy.', {
      description: 'Contact us at support@funcirclegames.com if you think this is a mistake.',
      duration: 10000,
    });
  };

  const fetchUser = async (options: { silent?: boolean, force?: boolean } = {}) => {
    // Priority 3: Throttle /api/auth/me calls to avoid redundant database reads
    const now = Date.now();
    const FETCH_TTL = 30 * 1000; // 30 seconds
    if (!options.force && user && (now - lastMeFetchRef.current < FETCH_TTL)) {
      console.log('AuthContext: Skipping redundant fetchUser, just validated recently.');
      return;
    }
    lastMeFetchRef.current = now;

    if (!options.silent) setLoading(true);
    try {
      const response = await apiFetch('/api/auth/me');
      if (response.ok) {
        const data = await response.json();
        const userWithPlan = { ...data.user, plan: data.user.plan || 'FREE' };
        
        if (data.user.is_suspended) {
          await logout();
          showSuspendedAlert();
          return;
        }
        
        setUser(userWithPlan);
        setProfile(userWithPlan);
        const now = Date.now();
        setLastRefreshTime(now);
        lastRefreshRef.current = now;
        if (data.accessExpiresAt) setAccessExpiresAt(data.accessExpiresAt);
        if (data.lastAuthValidation) setLastAuthValidation(data.lastAuthValidation);
        localStorage.setItem('user_profile_cache', JSON.stringify(sanitizeUserForCache(userWithPlan)));
      } else if (response.status === 401 || response.status === 403) {
        const data = await response.json().catch(() => ({}));
        if (data.error === 'Session expired' || data.error?.includes('another device')) {
          toast.error('Session Expired', {
            description: 'You were logged out because your account was used on another device.',
            duration: 8000,
          });
        }
        
        setUser(null);
        setProfile(null);
        setAccessExpiresAt(null);
        localStorage.removeItem('user_profile_cache');
      }
    } catch (error) {
      // Silent catch
    } finally {
      setLoading(false);
    }
  };

  const silentRefresh = async () => {
    if (!user) return;
    try {
        console.log('AuthContext: Proactive silent refresh started...');
        const response = await apiFetch('/api/auth/refresh', { method: 'POST' });
        if (response.ok) {
            const data = await response.json();
            console.log('AuthContext: Proactive refresh successful');
            const now = Date.now();
            setLastRefreshTime(now);
            lastRefreshRef.current = now;
            if (data.accessExpiresAt) setAccessExpiresAt(data.accessExpiresAt);
            if (data.lastAuthValidation) setLastAuthValidation(data.lastAuthValidation);
        } else if (response.status === 401) {
            console.warn('AuthContext: Session expired during silent refresh');
            const data = await response.json().catch(() => ({}));
            if (data.error === 'Session expired' || data.error?.includes('another device')) {
                toast.error('Session Expired', {
                    description: 'You were logged out because your account was used on another device.',
                    duration: 8000,
                });
            }
            setUser(null);
            setProfile(null);
            setAccessExpiresAt(null);
            localStorage.removeItem('user_profile_cache');
        }
    } catch (e) {
        console.error('AuthContext: Silent refresh error:', e);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      // PROACTIVE REFRESH AVOIDANCE OF 401s
      // If we have a cached profile, we were likely logged in.
      // Call silentRefresh() FIRST before fetchUser to ensure token is fresh.
      const cached = localStorage.getItem('user_profile_cache');
      if (cached) {
        console.log('AuthContext: Cached session found, proactively refreshing token...');
        await silentRefresh();
      }

      // Initial fetch user (SILENT by default now to avoid full-screen spinner on load)
      await fetchUser({ silent: true });
    };

    initAuth();

    // 1. Set up proactive refresh every 10 minutes (Best practice for 15-min tokens)
    const TEN_MINUTES = 10 * 60 * 1000;
    const intervalId = setInterval(silentRefresh, TEN_MINUTES);

    // 2. Refresh on window focus ONLY IF more than 5 minutes have passed since last refresh
    const handleFocus = () => {
        const now = Date.now();
        const FIVE_MINUTES = 5 * 60 * 1000;
        
        if (now - lastRefreshRef.current > FIVE_MINUTES) {
            console.log('AuthContext: Tab returned after >5m, proactively refreshing session...');
            silentRefresh();
        } else {
            console.log('AuthContext: Tab returned recently, skipping proactive refresh.');
        }
    };
    window.addEventListener('focus', handleFocus);

    return () => {
        clearInterval(intervalId);
        window.removeEventListener('focus', handleFocus);
    }
  }, []);

  const login = async (email: string, password: string, turnstileToken?: string) => {
    console.log('login: starting for', email);
    const response = await apiFetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, turnstile_token: turnstileToken }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      console.error('login: failed:', error.error);
      
      if (response.status === 403) {
        if (error.error?.includes('suspended')) {
          showSuspendedAlert();
        }
        if (error.requiresCaptcha) {
          return { requiresCaptcha: true };
        }
      }
      
      throw new Error(error.error || 'Login failed');
    }
    const data = await response.json();
    console.log('login: success');
    const userWithPlan = data.user ? { ...data.user, plan: data.user.plan || 'FREE' } : null;
    
    if (userWithPlan?.is_suspended) {
      await logout();
      showSuspendedAlert();
      throw new Error('This account is suspended.');
    }

    if (userWithPlan) {
      localStorage.setItem('user_profile_cache', JSON.stringify(sanitizeUserForCache(userWithPlan)));
    }

    if (data.mfa_required) {
      return { mfa_required: true, user_id: data.user_id };
    }

    setUser(userWithPlan);
    setProfile(userWithPlan);
    if (data.accessExpiresAt) setAccessExpiresAt(data.accessExpiresAt);
    if (data.lastAuthValidation) setLastAuthValidation(data.lastAuthValidation);
  };

  const verifyMfa = async (userId: string, code: string, isBackupCode: boolean = false, turnstileToken?: string) => {
    const response = await apiFetch('/api/auth/mfa-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, code, isBackupCode, turnstileToken }),
    });

    if (!response.ok) {
      const error = await response.json();
      
      if (response.status === 403 && error.requiresCaptcha) {
        return { requiresCaptcha: true };
      }
      
      throw new Error(error.error || 'MFA verification failed');
    }

    const data = await response.json();
    const userWithPlan = { ...data.user, plan: data.user.plan || 'FREE' };
    
    if (userWithPlan.is_suspended) {
      await logout();
      showSuspendedAlert();
      throw new Error('This account is suspended.');
    }

    setUser(userWithPlan);
    setProfile(userWithPlan);
    if (data.accessExpiresAt) setAccessExpiresAt(data.accessExpiresAt);
    if (data.lastAuthValidation) setLastAuthValidation(data.lastAuthValidation);
    localStorage.setItem('user_profile_cache', JSON.stringify(sanitizeUserForCache(userWithPlan)));
  };

  const signup = async (email: string, password: string, username: string, turnstileToken?: string) => {
    const response = await apiFetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, username, turnstile_token: turnstileToken }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      if (response.status === 403 && error.requiresCaptcha) {
        return { requiresCaptcha: true };
      }
      throw new Error(error.error || 'Signup failed');
    }
    const data = await response.json();
    return data;
  };

  const loginWithGoogle = async () => {
    try {
      console.log('loginWithGoogle: fetching URL...');
      const fromOrigin = window.location.origin;
      const response = await apiFetch(`/api/auth/google/url?from=${encodeURIComponent(fromOrigin)}`);
      if (!response.ok) {
        const errData = await response.json().catch(() => ({ error: 'Failed to fetch auth URL' }));
        console.error('loginWithGoogle: failed to get URL:', errData);
        toast.error(`Failed to start Google Login: ${errData.error || response.statusText}`);
        return;
      }
      const { url } = await response.json();
      console.log('loginWithGoogle: redirecting to:', url);
      // Redirect the same page instead of opening a popup
      window.location.href = url;
    } catch (error: any) {
      console.error('loginWithGoogle: error:', error);
      toast.error(`An error occurred: ${error.message}`);
    }
  };

  const logout = async () => {
    try {
      await apiFetch('/api/auth/logout', { 
        method: 'POST', 
      });
    } catch (e) {
      console.error('Logout API error:', e);
    }
    localStorage.removeItem('user_profile_cache');
    setUser(null);
    setProfile(null);
    setAccessExpiresAt(null);
  };

  const updateUsername = async (username: string) => {
    console.log('updateUsername: starting for', username);
    const response = await apiFetch('/api/auth/update-profile', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username })
    });
    
    console.log('updateUsername: response status:', response.status);
    
    const text = await response.text();
    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch (e) {
      console.error('updateUsername: Failed to parse JSON response:', text);
      throw new Error('Server returned an invalid response. Please try again.');
    }

    if (!response.ok) {
      console.error('updateUsername: failed:', data.error || data.message);
      throw new Error(data.error || data.message || 'Update failed');
    }
    
    console.log('updateUsername: success');
    const updatedUser = user ? { ...user, username, needs_username_setup: 0 } : null;
    setUser(updatedUser);
    setProfile(updatedUser);
    if (updatedUser) {
      localStorage.setItem('user_profile_cache', JSON.stringify(sanitizeUserForCache(updatedUser)));
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    const response = await apiFetch('/api/auth/change-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ currentPassword, newPassword })
    });
    
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to change password');
    }
  };

  const updateUserPlan = async (plan: 'FREE' | 'PRO' | 'ELITE' | 'ENTERPRISE') => {
    // In a real app, this would hit /api/auth/update-plan
    // For now we simulate it by updating state and localStorage
    const updatedUser = user ? { ...user, plan } : null;
    setUser(updatedUser);
    setProfile(updatedUser);
    if (updatedUser) {
      localStorage.setItem('user_profile_cache', JSON.stringify(sanitizeUserForCache(updatedUser)));
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      profile, 
      loading, 
      accessExpiresAt,
      lastAuthValidation,
      sessionHealthy,
      login, 
      verifyMfa,
      signup, 
      loginWithGoogle, 
      logout, 
      updateUsername, 
      changePassword, 
      updateUserPlan, 
      refreshUser: fetchUser 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
